/**
 * StochRSIPanel — Stochastic RSI (params from indicatorConfigStore, default
 * 14,14,3,3) in its own chart below the main
 * chart, the second single-slot sub-panel indicator (indicatorStore.activeSubPanel).
 *
 * A copy of RSIPanel's pattern (chart instance, index-based two-way sync,
 * 0-100 pinned scale, axis-width match, replay filter) with two line series,
 * %K and %D, computed client-side by utils/klineAnalytics.computeStochRSI.
 */

import { useEffect, useRef, useState } from 'react';
import {
  createChart, LineStyle,
  type IChartApi, type IPriceLine, type ISeriesApi, type LineWidth,
} from 'lightweight-charts';
import { useMarketStore } from '../../store/marketStore';
import { useReplayStore } from '../../store/replayStore';
import { useThemeStore } from '../../store/themeStore';
import { useIndicatorConfigStore, type LineStyle as LineStyleCfg } from '../../store/indicatorConfigStore';
import { toChartTime } from '../../utils/chartTime';
import { computeStochRSI, type RSIPoint } from '../../utils/klineAnalytics';
import { subChartThemeOptions } from './DeltaPanel';
import { linkSubPanel, SUB_PANEL_INTERACTION } from './subPanelSync';
import { SubPanelDrawingPane } from './SubPanelDrawingPane';
import { AXIS_MIN_WIDTH, refLineColors } from './RSIPanel';

interface StochRSIPanelProps {
  /** Ref to the main candlestick chart — this panel follows its time scale. */
  sharedChartRef: React.RefObject<IChartApi | null>;
}

export function StochRSIPanel({ sharedChartRef }: StochRSIPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef     = useRef<IChartApi | null>(null);
  const kSeriesRef   = useRef<ISeriesApi<'Line'> | null>(null);
  const dSeriesRef   = useRef<ISeriesApi<'Line'> | null>(null);
  // Line-less series that carries the reference levels, so hiding %K in
  // settings doesn't take the 80/50/20 lines with it.
  const refSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const bandLinesRef = useRef<IPriceLine[]>([]);   // 80 / 20
  const midLineRef   = useRef<IPriceLine | null>(null);  // 50

  // Gates the drawing pane and auto-scale button, which need the chart to exist on mount.
  const [chartReady, setChartReady] = useState(false);
  const [current, setCurrent] = useState<{ k: number; d: number | null } | null>(null);

  const candles          = useMarketStore((s) => s.candles);
  const theme            = useThemeStore((s) => s.theme);
  const replayActive     = useReplayStore((s) => s.isActive);
  const replayCursorTime = useReplayStore((s) => s.cursorTime);
  const cfg              = useIndicatorConfigStore((s) => s.configs.stochRsi);

  // Same as RSIPanel: keep this panel's price axis as wide as the main chart's
  // so the two plot areas (and their bars) line up vertically.
  const matchAxisWidth = () => {
    const main  = sharedChartRef.current;
    const chart = chartRef.current;
    if (!main || !chart) return;
    const w = main.priceScale('right').width();
    chart.priceScale('right').applyOptions({ minimumWidth: Math.max(w, AXIS_MIN_WIDTH) });
  };

  // ── Chart initialisation + time-scale sync ────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;

    const themeOpts = subChartThemeOptions(theme);
    const chart = createChart(containerRef.current, {
      layout: themeOpts.layout,
      grid: themeOpts.grid,
      crosshair: themeOpts.crosshair,
      leftPriceScale: {
        visible: false,
      },
      rightPriceScale: {
        borderColor: themeOpts.rightPriceScale.borderColor,
        scaleMargins: { top: 0.05, bottom: 0.05 },
        minimumWidth: AXIS_MIN_WIDTH,
      },
      timeScale: {
        borderColor: themeOpts.timeScale.borderColor,
        timeVisible: true,
        secondsVisible: false,
      },
      // Scrolls/zooms like the main chart; subPanelSync links the two ranges.
      ...SUB_PANEL_INTERACTION,
      width:  containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
    });

    const baseOpts = {
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      priceFormat: { type: 'price' as const, precision: 2, minMove: 0.01 },
      // Pin the scale to the oscillator's full 0-100 range.
      autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: 100 } }),
    };
    const refSeries = chart.addLineSeries({ ...baseOpts, lineVisible: false, lastValueVisible: false });
    // Read the config imperatively: edits restyle/recompute the existing
    // series in the effects below rather than recreating the chart.
    const [kLine, dLine] = useIndicatorConfigStore.getState().configs.stochRsi.lines;
    const lineSeries = (style: LineStyleCfg) => chart.addLineSeries({
      ...baseOpts,
      color: style.color,
      lineWidth: style.width as LineWidth,
      visible: style.visible,
      lastValueVisible: true,
    });
    const kSeries = lineSeries(kLine);
    const dSeries = lineSeries(dLine);

    // Price lines survive setData, so the reference levels are created once —
    // on the never-hidden ref series, so they aren't drawn twice.
    const colors = refLineColors(theme);
    const refLine = (price: number, color: string, lineStyle: LineStyle) =>
      refSeries.createPriceLine({
        price, color, lineWidth: 1, lineStyle, axisLabelVisible: false, title: '',
      });
    bandLinesRef.current = [
      refLine(80, colors.band, LineStyle.Dashed),
      refLine(20, colors.band, LineStyle.Dashed),
    ];
    midLineRef.current = refLine(50, colors.mid, LineStyle.Dotted);

    chartRef.current   = chart;
    kSeriesRef.current = kSeries;
    dSeriesRef.current = dSeries;
    refSeriesRef.current = refSeries;

    // Two-way sync by logical (bar index) range, as in RSIPanel: the
    // data effect pads both series to the candle count, so index N is the same
    // bar on both charts.
    const unlink = linkSubPanel(sharedChartRef, chart, containerRef.current, matchAxisWidth);
    setChartReady(true);

    const observer = new ResizeObserver(() => {
      if (!containerRef.current) return;
      chart.applyOptions({
        width:  containerRef.current.clientWidth,
        height: containerRef.current.clientHeight,
      });
    });
    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      unlink();
      setChartReady(false);
      chart.remove();
      chartRef.current   = null;
      kSeriesRef.current = null;
      dSeriesRef.current = null;
      refSeriesRef.current = null;
      bandLinesRef.current = [];
      midLineRef.current   = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedChartRef]);

  // Re-skin the native chart (grid/axis/crosshair + reference-line colors) on
  // theme change without recreating it, so data and range survive the switch.
  useEffect(() => {
    if (!chartRef.current) return;
    const themeOpts = subChartThemeOptions(theme);
    chartRef.current.applyOptions({
      layout: themeOpts.layout,
      grid: themeOpts.grid,
      crosshair: themeOpts.crosshair,
      leftPriceScale: { borderColor: themeOpts.leftPriceScale.borderColor },
      rightPriceScale: { borderColor: themeOpts.rightPriceScale.borderColor },
      timeScale: { borderColor: themeOpts.timeScale.borderColor },
    });
    const colors = refLineColors(theme);
    bandLinesRef.current.forEach((l) => l.applyOptions({ color: colors.band }));
    midLineRef.current?.applyOptions({ color: colors.mid });
  }, [theme]);

  // ── Recompute on every candles change (incl. the forming bar's ticks) ─────
  // and whenever the lengths / smoothing are edited.
  useEffect(() => {
    const kSeries   = kSeriesRef.current;
    const dSeries   = dSeriesRef.current;
    const refSeries = refSeriesRef.current;
    if (!kSeries || !dSeries || !refSeries) return;

    // In replay, marketStore still holds the full window — slice at the cursor
    // exactly as ReplayEngine does for the main chart, so bar counts match.
    const visible = replayActive && replayCursorTime != null
      ? candles.filter((c) => c.t <= replayCursorTime)
      : candles;

    const { k, d } = computeStochRSI(visible, cfg.rsiLength, cfg.stochLength, cfg.kSmooth, cfg.dSmooth);

    // %D starts dSmooth-1 bars after %K, so each series gets its own
    // whitespace padding — both end up exactly visible.length entries long,
    // which keeps them index-aligned with the main chart.
    const padded = (points: RSIPoint[]) => [
      ...visible.slice(0, visible.length - points.length).map((c) => ({ time: toChartTime(c.t) })),
      ...points.map((p) => ({ time: toChartTime(p.time), value: p.value })),
    ];
    // The ref series needs data of its own for its price lines to render.
    const kData = padded(k);
    kSeries.setData(kData);
    refSeries.setData(kData);
    dSeries.setData(padded(d));

    // setData can refit this chart's own range — pull it back to the main one.
    const mainRange = sharedChartRef.current?.timeScale().getVisibleLogicalRange();
    if (mainRange) chartRef.current?.timeScale().setVisibleLogicalRange(mainRange);
    matchAxisWidth();

    setCurrent(k.length ? { k: k.at(-1)!.value, d: d.at(-1)?.value ?? null } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candles, replayActive, replayCursorTime, sharedChartRef,
      cfg.rsiLength, cfg.stochLength, cfg.kSmooth, cfg.dSmooth]);

  // ── Re-style on color/width/visibility edits (no recompute) ───────────────
  useEffect(() => {
    const [kLine, dLine] = cfg.lines;
    kSeriesRef.current?.applyOptions({ color: kLine.color, lineWidth: kLine.width as LineWidth, visible: kLine.visible });
    dSeriesRef.current?.applyOptions({ color: dLine.color, lineWidth: dLine.width as LineWidth, visible: dLine.visible });
  }, [cfg.lines]);

  const [kLine, dLine] = cfg.lines;
  const showK = current !== null && kLine.visible;
  const showD = current !== null && current.d !== null && dLine.visible;

  return (
    <div className="relative w-full h-full" data-drawing-pane="stochrsi">
      {/* Stat overlay */}
      <div className="absolute top-1 left-3 z-10 flex items-center gap-1.5 pointer-events-none select-none text-xs font-mono">
        <span className="text-[var(--text-muted)] font-sans">
          Stoch RSI ({cfg.rsiLength},{cfg.stochLength},{cfg.kSmooth},{cfg.dSmooth})
        </span>
        {(showK || showD) && <span className="text-[var(--text-muted)]">·</span>}
        {showK && (
          <>
            <span className="text-[var(--text-muted)] font-sans">K</span>
            <span style={{ color: kLine.color }}>{current!.k.toFixed(2)}</span>
          </>
        )}
        {showD && (
          <>
            <span className="text-[var(--text-muted)] font-sans">D</span>
            <span style={{ color: dLine.color }}>{current!.d!.toFixed(2)}</span>
          </>
        )}
      </div>

      <div ref={containerRef} className="w-full h-full" />

      {/* Drawings + "A" (auto-scale) button + style toolbar. */}
      {chartReady && (
        <SubPanelDrawingPane paneId="stochrsi" kind="stochrsi" chartRef={chartRef} seriesRef={refSeriesRef} sharedChartRef={sharedChartRef} />
      )}
    </div>
  );
}
